export interface BlogChapter {
  id: string;
  title: string;
  content: string;
}

export interface BlogPost {
  slug: string;
  character: string;
  characterAlias: string;
  characterColor: string;
  characterTag: string;
  entryNumber: string;
  title: string;
  subtitle: string;
  timeline: string;
  excerpt: string;
  image: string;
  coverImage: string;
  date: string;
  readTime: string;
  chapters: BlogChapter[];
}

export const blogPosts: BlogPost[] = [
  {
    slug: "jordan-college-days",
    character: "Jordan Reyes",
    characterAlias: '"The Wraith"',
    characterColor: "#C8922A",
    characterTag: "Jordan",
    entryNumber: "Entry #001",
    title: "Before the Bat",
    subtitle: "Jordan Reyes — The College Years",
    timeline: "Pre-Outbreak · Year −4 to −1",
    excerpt:
      "Before the barbed wire and the silence, there was a girl with a killer pitching arm, a full athletic scholarship, and a stubborn refusal to let the world beat her down. This is who Jordan Reyes was before the world ended.",
    image:
      "https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/c05dffc4-d44f-41ba-a84d-b2cd8f6693d3_openart-image_1774894787160_6ebff0aa_1774894787312_c3b2fd83.png?v=62526325eba679459c6686a6df286992",
    coverImage:
      "https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/c05dffc4-d44f-41ba-a84d-b2cd8f6693d3_openart-image_1774894787160_6ebff0aa_1774894787312_c3b2fd83.png?v=62526325eba679459c6686a6df286992",
    date: "Lore Archive · 2026",
    readTime: "12 min read",
    chapters: [
      {
        id: "ch-01",
        title: "Chapter I — The Silent Storm",
        content: `## On the Field

Jordan Reyes was not the loudest player on the diamond. She never needed to be. Coaches called her a "gamer" — the kind of athlete whose focus sharpened like a blade when the pressure rose, when the inning was full, when the crowd stopped breathing. Scouts wrote notes in the margins of their clipboards. Words like *relentless* and *composure beyond her age.*

Her signature was a rise ball that seemed to defy physics. Batters would lean into it, read the spin wrong, commit — and then the ball would vanish into a different zip code. She'd gotten three Division I starting pitchers out that way in her freshman exhibition season. Word got around fast.

## Work Ethic

But the rise ball wasn't a gift. It was a debt paid in advance, daily, for years.

Her teammates knew her as the first one in and the last one out. While others groaned through conditioning drills, Jordan moved in near-silence — a private ritual, almost sacred. She treated the training facility like a church she'd built herself, one early morning at a time. There was no complaining. There was no coasting. The scholarship wasn't just tuition and room and board. It was a promise.

## The Promise

She had made it to her mother the night before she left for campus — Elena Reyes sitting at the kitchen table in her nursing scrubs, hands wrapped around a coffee mug, eyes tired but proud in a way that had nothing to do with trophies.

*"Build something better, mija. For all of us."*

Jordan kept that sentence taped to the inside of her locker door. Every pitcher who faced her that season, without knowing it, was facing that sentence too.`,
      },
      {
        id: "ch-02",
        title: "Chapter II — The Weight She Carries",
        content: `## Off the Field

Away from the diamond, Jordan was a different kind of force — quieter, steadier, but no less present.

She majored in Kinesiology with a pre-Physical Therapy track. The choice wasn't academic ambition for its own sake. She was fascinated by the human body — not just how it performed, but how it broke down, and how it healed. Her mother had worked decades of manual labor. The chronic pain in Elena's lower back wasn't something doctors had time to fully address. Jordan intended to be the person who did.

She carried bandaids and ibuprofen in her bag at all times — a habit that made her teammates tease her gently. *"You're like a walking CVS, Reyes."* She'd shrug. It wasn't even a conscious choice anymore. Someone was always going to need something.

## The Reliable One

She was never the life of the party. She was something rarer: the person you actually wanted around when things got hard.

She was the one who made sure everyone got home after team dinners. The one who quietly helped a struggling freshman with their stats homework at midnight before a home game the next morning. She had a small, tight-knit group of friends — mostly teammates, people who'd earned her trust the slow way, through shared exhaustion and honest conversations in the back of the team van.

She didn't confide easily. But once you were in, you were in for good.

## Her Burden

What people didn't see — what she made sure they didn't see — was the arithmetic she ran in her head every month.

A portion of her athletic stipend went home. Not all of it, but enough to matter. She'd worked out exactly how much she could send without falling behind on her own expenses, and she recalculated every semester as costs shifted. Her siblings texted her daily. Dani, navigating high school with their mother working nights. Marco, barely ten, sending voice memos that were mostly just him laughing at his own jokes.

Her phone's lock screen was a photo of the two of them, taken at a Little League game the summer before she'd left for college. Marco had a streak of red dirt across his cheek. Dani was making a face at the camera. It was the most ordinary, perfect photograph she owned.

She checked it between innings.`,
      },
      {
        id: "ch-03",
        title: "Chapter III — The Wandering Skillet",
        content: `## Tuesday After Double Practice

It was a sweltering Tuesday. Jordan had thrown two full practice sessions back to back — a brutal double that Coach Harmon had called "character building" and that Jordan privately called something less printable. She'd blown a pitch in the second session, a rise ball that stayed flat, and the phantom of it was still living in her shoulder as she walked across campus.

She was exhausted. She was frustrated. She wanted to disappear into her dorm and not speak to anyone for approximately twelve hours.

The smell hit her first.

Garlic. Herbs. Something rich and savory cutting clean through the bland ambient air of the campus quad — the kind of smell that bypassed the brain entirely and went straight to something older and more honest. She stopped walking without deciding to.

She followed it.

## The Wandering Skillet

The food truck was parked at the edge of the quad, brightly painted in yellows and greens, a hand-lettered sign arching across the awning: **The Wandering Skillet.**

The guy working it was all limbs and energy — tall, moving with the unconscious efficiency of someone who'd worked a small space for years, juggling pans, calling out an order, and delivering a terrible joke to the customer ahead of Jordan simultaneously. He didn't drop a single thing. He looked like the kind of person who found joy genuinely easy, which Jordan found vaguely suspicious.

The customer ahead of her walked away laughing. Jordan stepped up.

SPEAKER:AIDAN (to previous customer, turning to Jordan)
> "What do you call a fake noodle? An impasta! ...Tough crowd. Okay, for you, my friend, extra cheese to heal your wounded funny bone."

He turned to her. His smile didn't shift when he clocked her expression — the flat, end-of-day exhaustion, the set jaw, the thousand-yard stare of someone who had nothing left to give a conversation.

SPEAKER:AIDAN
> "You look like you just struck out the side and lost the game. I have just the thing. My 'You-Will-Feel-Better-After-This' Grilled Cheese. Secret ingredient: a little bit of hope. And three types of cheese."

Jordan, who in any other circumstance would have deflected or moved on, was too depleted to manage even that. She just nodded.

## The Sandwich

He cooked while he talked — not with the nervous energy of someone filling silence, but with the easy confidence of someone who genuinely liked the sound of conversation. He asked about her day without prying. He made observations about campus life that were so absurdly accurate she found herself listening despite herself.

When she let out a small, surprised laugh at something he said, she felt it as a physical thing — like a knot in her chest she hadn't known was there, loosening a degree or two.

He handed her the sandwich wrapped in wax paper. It was golden brown, pressed to a perfect crisp, oozing cheese from every edge. A tiny pickle spear on the side, secured with a toothpick and a small paper flag.

SPEAKER:AIDAN
> "The pickle is for balance. Acid cuts through the richness. It's not optional."

She took a bite standing at the counter.

It was, without any qualification or exaggeration, the best thing she had ever eaten.

## What Clicked

Something shifted.

She stood there in the late afternoon heat, holding a grilled cheese sandwich, and understood something she couldn't quite name yet but would spend years unpacking: here was someone who put the same intensity, the same care, the same silent *seriousness* into creating comfort as she put into winning games. He wasn't making food. He was performing an act of deliberate kindness — he had seen her frustration and met it not with pity, not with advice, but with melted cheese and a dumb joke and a pickle spear for balance.

He didn't know she was the starting pitcher. He didn't know about the scholarship, or the stipend she sent home, or the photograph of her siblings on her lock screen. He just saw a tired girl who needed a good meal, and he gave her one.

## The Foundation

Their college romance was built on exactly that balance — two people with different kinds of intensity, teaching each other what the other couldn't give themselves.

She dragged him to the gym and showed him how to properly lift weights so he wouldn't throw out his back hauling propane tanks. He dragged her to art film festivals and farmer's markets on Saturday mornings, forcing her — gently, patiently — to slow down and notice that life existed beyond the diamond's edges.

She became his taste-tester for every new creation. Her athlete's palate was brutally honest and invaluable in equal measure. She'd take a bite, go quiet, and deliver a verdict like: *"Needs more acid. Maybe a lemon aioli?"* He started keeping notes.

He became her sanctuary. After high-pressure games, his tiny off-campus apartment was the one place in the world where nobody wanted to talk about softball. They'd sit on his secondhand couch and watch bad cooking competition shows and do absolutely nothing of consequence. She slept better there than anywhere else.

!! This period of her life was the most important one. Not because of the wins. Not because of the scouts. Because it taught her that strength wasn't only physical power and relentless drive — true strength was also the vulnerability to accept kindness, the wisdom to rest, the courage to build something gentle in a world that valued hard achievements.

She learned that lesson in the safety of Aidan's world. In the apocalypse, it became her armor. The memory of that peace — of a golden brown sandwich and a dumb joke and a pickle spear for balance — is what she fights so desperately to get back.`,
      },
    ],
  },
  {
    slug: "aidan-college-days",
    character: "Aidan Lee",
    characterAlias: '"The Chef"',
    characterColor: "#7FAF6A",
    characterTag: "Aidan",
    entryNumber: "Entry #002",
    title: "The Wandering Skillet",
    subtitle: "Aidan Lee — Before the End",
    timeline: "Pre-Outbreak · Culinary School to Day Zero",
    excerpt:
      "He was the culinary school misfit who cooked for the janitorial staff after hours and argued with French instructors about the philosophical merits of a perfect street taco. He believed food was about memory and emotion, not technique. Then the world ended — and he found out he was right.",
    image:
      "https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/c39b1003-5ee3-4b71-9c11-eda3a1278883_openart-image_1774886107147_ed7c8e03_1774886107216_b2ffd6ff.png?v=8b8dc028a5bccd810f5334b62c4354b5",
    coverImage:
      "https://storage.readdy-site.link/project_files/4353f4cf-bd60-4a53-b8de-9b86b4213d15/c39b1003-5ee3-4b71-9c11-eda3a1278883_openart-image_1774886107147_ed7c8e03_1774886107216_b2ffd6ff.png?v=8b8dc028a5bccd810f5334b62c4354b5",
    date: "Lore Archive · 2026",
    readTime: "13 min read",
    chapters: [
      {
        id: "ch-01",
        title: "Chapter I — The Misfit Saucier",
        content: `## The Institute

The culinary institute Aidan attended was the kind of place that used the word "classical" the way other institutions used the word "sacred." The curriculum moved through Escoffier like scripture. Knife skills were graded with a ruler. The head French instructor, Chef Moreau, had opinions about butter that bordered on the theological.

Aidan respected all of this. He just couldn't stop questioning it.

While his classmates bent over their cutting boards perfecting the brunoise, Aidan was in the back corner running a quiet experiment: what happened if you added a spoonful of kimchi to a grilled cheese? What did the taco truck down the street understand about flavor balance that a three-star kitchen didn't? Why did a dish built on instinct and memory so often land harder than one built on three hours of technical precision?

His knife skills were, as Chef Moreau noted in two separate evaluations, *"functional but not beautiful."* Aidan had circled that note and taped it to his locker. He considered it accurate.

## Strengths, Honestly Assessed

Where he was genuinely brilliant: flavor theory. He had a palate that his instructors called extraordinary — an almost synesthetic ability to taste a dish and immediately understand what it wanted, what it was missing, what one small addition would make it whole. He could reverse-engineer a sauce from memory. He could smell a spice and place its origin within a region.

He was unexpectedly gifted at pastry — a discipline that surprised everyone, including him, because pastry demanded the kind of precision that ran counter to his personality. But he understood, eventually, that pastry's precision wasn't a cage. It was a different language. And he'd always liked learning languages.

Under pressure, he was ice. The student-run restaurant had a dinner service every Thursday — real paying customers, real consequences. Students cracked under the ticket printer's rhythm. Aidan moved through the chaos with a particular calm that looked, from the outside, almost like indifference. It was not indifference. It was focus so deep it resembled stillness.

## The Beef Bourguignon Incident

The moment that became legend in his cohort involved a beef bourguignon and Chef Moreau and a theory Aidan had about coffee.

The argument began, as most kitchen arguments do, with a small suggestion.

SPEAKER:AIDAN (to Chef Moreau, mid-service)
> "I've been thinking — a teaspoon of espresso in the braise. Just to deepen the umami. Not to make it taste like coffee. The way chocolate does in a mole."

Chef Moreau's response took approximately four minutes and covered the history of Burgundian cuisine, the philosophical crime of adulterating a classical preparation, and the suggestion that Aidan perhaps consider a career in fusion, which he said the way other people said *amateur.*

Aidan listened patiently to all of it. Then:

SPEAKER:AIDAN
> "With respect — there's a woman on the corner of Fifth and Market who makes a beef taco for three dollars that would embarrass this bourguignon. I'm trying to figure out what she knows that we don't."

He received a written citation for insubordination. He also, two weeks later, made the bourguignon with espresso for the staff meal. Three instructors asked for the recipe. Chef Moreau did not, but he had a second bowl.

## What He Believed

His philosophy crystallized during his second year, in a habit that began by accident and became ritual.

After evening service, when the kitchen had been broken down and the other students had gone to decompress at the bar down the street, Aidan would stay. The janitorial staff worked the building from ten to midnight — a small rotating crew of people who had been invisible to most of the institute's population all semester.

He started cooking for them.

Not elaborate things — whatever was left over, whatever he was curious about. He'd set a plate on the counter and ask: *"What was your favorite thing your mom or grandma cooked? What does it taste like when you close your eyes?"*

Then he'd try to make it.

He was often wrong. Sometimes he was close enough that it made someone go quiet in a way that wasn't unhappy. Those were the nights that mattered.

!! Food is successful not when it is technically perfect — but when it makes someone feel something. Comfort. Joy. Nostalgia. Surprise. The memory of a person who loved you enough to cook for you. That belief, formed in a kitchen after midnight, would drive every decision he made for the rest of his life.`,
      },
      {
        id: "ch-02",
        title: "Chapter II — The Truck",
        content: `## After Graduation

His classmates scattered in the predictable directions: loans for restaurant ventures, moving trucks to New York, 80-hour weeks in the kind of kitchens where yelling was considered a teaching method. A few went to work for hotels. One went back to school.

Aidan bought a beat-up step van from a guy who was abandoning it for reasons he described as "deeply personal but also mechanical."

His savings were meager. The loan was small and terrifying in the way only loans that depend entirely on your own ability to succeed can be. He spent three weeks stripping the van down to its bones, then another three weeks rebuilding the kitchen inside it. He did most of the welding himself after watching twelve hours of YouTube tutorials in a row.

## The Name

He went through forty-seven name ideas in a notebook before landing on *The Wandering Skillet.*

It was right the moment he wrote it. It hinted at travel, at movement, at a refusal to stay fixed in one place or one idea. And the skillet — not a sauté pan, not a copper rondeau, not anything that signaled fine dining — was the cast-iron skillet. Humble. Indestructible. Equally at home over a campfire or a professional range. The most versatile piece of cookware in any kitchen.

Not pretentious. Inviting. That was the whole philosophy in two words.

## The Menu

He called the concept "Elevated Comfort Food," which he described to anyone who asked as *"a love letter to global street food and American classics, filtered through four years of culinary training and profound disrespect for pretension."*

The Grand Slam Grilled Cheese anchored the menu. Three cheeses — gruyère, sharp white cheddar, fontina — with a caramelized onion jam he made in batches every two days and a whisper of truffle oil that he insisted was not a cliché in this specific application. It was the sandwich that made people stop mid-bite and get quiet.

Every week, the chalkboard above the window featured a Mystery Melt. The rules: use whatever was left, whatever he was curious about, whatever felt right that morning. The Morale-Boosting Mac & Cheese was born from a Mystery Melt that had been a mistake in the best possible way. The Breakfast Burrito of Champions — hash brown inside the wrap, structural integrity and deliciousness in one package — started as a joke and became the most-requested item by 7 AM on Fridays.

Dessert Nachos closed out many a late-night service: fried wonton chips with chocolate sauce, fresh fruit, and a sweetened lime crema that arrived alongside an explanation nobody asked for about why crema was the correct choice and why whipped cream would have been a betrayal.

## The Grind

What nobody tells you about running a food truck is how completely it consumes you.

He was the chef, the dishwasher, the marketer, the mechanic, and occasionally the therapist. He learned to fix a faulty generator at seven in the morning before a lunch rush that started in four hours. He developed what his one part-time employee, a college sophomore named Derek, called "a supernatural ability to manage ten things in a 4x8 foot space without losing his mind or anyone's order."

The chaos management was not learned in culinary school. It was built behind that counter, in real time, under real pressure, with real consequences. A missed order meant a hungry person. A broken burner meant a menu that didn't work. A late supplier meant improvisation or failure — and Aidan had decided early that failure was not a standing option.

He ran tight inventory. He mapped supplier routes. He knew, within half an hour, exactly how long any item on his menu would take at full volume. He maintained the truck's engine with the same care he gave his cast-iron skillets: methodical, consistent, deeply unglamorous.

It was the most useful education he ever received. He didn't know that yet.`,
      },
      {
        id: "ch-03",
        title: "Chapter III — The Best Customer",
        content: `## The Regular

She started showing up in the second month of the truck's run on campus.

She always ordered the same thing. The Grand Slam Grilled Cheese, no modifications, extra pickle spear. She ate it standing up, reading something on her phone, and was gone within fifteen minutes. She tipped well and said very little.

Aidan found this personally offensive.

Not the silence — he respected silence. It was the *same order, every time.* He had an entire menu. He had a Mystery Melt board that changed weekly and had been known to cause small arguments among regulars about what was on it. And this woman — strikingly athletic, carrying the kind of quiet intensity that suggested she was always about thirty seconds from sprinting somewhere — came to him for the same sandwich, day after day, like she was refueling rather than eating.

He took it as a challenge.

SPEAKER:AIDAN
> "The usual for the usual? I appreciate the loyalty, but my Breakfast Burrito of Champions is feeling neglected. It's got a hash brown *inside* the wrap. Think about it. Structural integrity *and* deliciousness."

She looked at him with the expression of someone who was deciding whether to laugh or walk away. She ordered the grilled cheese.

## Wearing Her Down

It took three weeks of persistent, entirely good-natured pestering.

He tried different angles. The philosophical argument ("You're not a person who repeats herself — so why the same order?"). The emotional appeal ("The Mystery Melt has never had a bad week. This week's is a green chile situation. I'm very proud of it."). The direct approach ("Point at the board. I'll make something you'll like. If you don't like it, the grilled cheese is on me.").

She eventually, on a Thursday afternoon when she looked particularly done with the world, pointed at the Mystery Melt board and said: *"Surprise me."*

It became their thing.

He'd build something he thought matched her — honest flavors, nothing fussy, something that rewarded attention. She'd eat it and deliver a review that was brief, accurate, and completely without diplomatic softening. *"Too sweet. Cut the honey."* *"This is right. Don't change it."* *"Needs more acid. Maybe a lemon aioli?"*

He started keeping notes. She was, without knowing it, the best culinary consultant he'd ever had.

## The First Date That Wasn't

It wasn't a formal date. He would tell this story for years and always begin it the same way: *"It wasn't a date. It was a breakdown. Mine, technically."*

The truck stalled on the campus service road on a Tuesday evening after a particularly punishing double service. He had one hand under the hood and the other holding a phone open to a generator repair forum when she walked by with a bag over her shoulder, heading wherever she went after practice.

She stopped. She looked at him. She looked at the truck.

SPEAKER:JORDAN
> "My dad was a mechanic. Move over. Hand me the wrench."

He moved over.

She found the problem in eleven minutes. He knew this because he watched every second of it: the economical way she worked, the absence of performance, the precise confidence of someone who had done this before and didn't need credit for it.

When the engine caught, she wiped her hands on a rag from her bag — she carried a rag, he noted, because of course she did — and handed back his wrench.

He cooked for her. Right there, on the service road, using everything left in the truck's prep, lit by the kitchen's interior lights and the last hour of daylight. Not the Grand Slam Grilled Cheese — she'd had that enough times. Something new, built around what he had and what she'd earned: a proper meal, the kind you make when you want someone to understand that you're paying attention.

They ate on the truck's rear bumper. She said almost nothing. He talked enough for both of them, which had always been their natural ratio. At some point the sky went fully dark and neither of them mentioned it.

It was the most romantic either of them had ever felt without using that word.

## What the Truck Built

When the outbreak hit, people assumed Aidan survived because he was resourceful. They were right, but they were thinking about the wrong kind of resourcefulness.

A can of beans, a stale spice, a foraged weed from the roadside? That was just a slow Tuesday at the Skillet. The truck had trained him to see possibility in scarcity — to look at random, limited ingredients and ask not *what's missing* but *what can this become?*

The lunch rush had trained him for the undead. That sounds like a joke. It isn't. A hundred and fifty hungry students in ninety minutes — the pressure, the noise, the need to prioritize, triage, keep moving, keep smiling — builds the exact neural architecture that keeps you functional when the stakes become actual survival.

And the counter — three years of standing behind a counter, making strangers feel seen and fed and welcome — had given him something no apocalypse could take: the ability to make people feel like a group before they had decided to be one. His humor wasn't a personality quirk. It was a tool he had spent years sharpening.

!! They started calling him The Chef in the second week after Day Zero. Not because he was cooking. Because he showed up. Every morning. When the world had stopped working, when the reasons to stop were accumulating faster than the reasons to continue — he showed up and he made something from what was left. That had always been his job. The apocalypse just raised the stakes.

## What He Carries

He still has the notebook from culinary school. The restaurant plans are buried under supply routes, plant identification charts, and notes on fire-to-fuel ratios for different wood types. But in the back, under a strip of tape he hasn't lifted since before the outbreak, there's a small pencil sketch.

Twelve tables. No printed menu. A door propped open to the street.

Somewhere in every meal he makes now — every scavenged, improvised, barely-enough thing he coaxes into something worth eating — there is still a grilled cheese. Three cheeses. Caramelized onion if he can get it. A tiny pickle spear on the side.

SPEAKER:AIDAN
> "The pickle is for balance. Acid cuts through the richness. It's not optional."

He says it like a joke. It is also a prayer. The memory of a service road at dusk, a woman who fixed his truck without being asked, and a meal eaten on a bumper while the campus lights came on one by one around them.

He is cooking back toward that bumper. He doesn't know if he'll make it.

He keeps cooking anyway.`,
      },
    ],
  },
];
